/**
 * Project icon set — Lucide React aliases.
 *
 * Centralising imports here lets us:
 * 1. Standardise stroke-width / size defaults across the app.
 * 2. Swap out the icon library later without touching every component.
 * 3. Keep tree-shaking honest — only icons re-exported here ship to users.
 *
 * All icons render as inline SVG with `currentColor`, so style with CSS
 * `color:` on a parent. Default stroke width is 1.5 (hairline) per the
 * Phase 1 design language. Override per-instance with the `strokeWidth`
 * prop when a heavier weight is required (e.g. `strokeWidth={2.25}` on
 * a primary CTA icon for visual balance).
 *
 * Naming uses the source Lucide name where the meaning is clear, and a
 * project-friendly alias where Lucide's name is generic. Add an alias
 * here rather than re-creating one inline.
 */
import {
  ShoppingCart,
  Heart,
  Menu,
  X,
  ArrowUp,
  Share2,
  Search,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  ChevronUp,
  User,
  Mail,
  Phone,
  MapPin,
  Plus,
  Minus,
  Check,
  AlertCircle,
  CheckCircle2,
  Trash2,
  Edit2,
  ExternalLink,
  Copy,
  Filter,
  Globe,
  Truck,
  Banknote,
  Clock,
  MessageCircle,
  SlidersHorizontal,
  ArrowUpDown,
  ShieldCheck,
  RotateCcw,
  Star,
  Eye,
  Lock,
  Volume2,
  VolumeX,
  LayoutDashboard,
  ClipboardList,
  Receipt,
  Package,
  FolderOpen,
  Gift,
  TrendingUp,
  Baby,
  Users,
  Tag,
  Image,
  GalleryHorizontal,
  Megaphone,
  UserCog,
  Settings,
  HelpCircle,
  Store,
  Monitor,
  Tablet,
  Smartphone,
  GripVertical,
  Layers3,
  Save,
  CopyPlus,
  EyeOff,
  PanelRight,
  FileText,
  Boxes,
  Undo2,
  Redo2,
} from 'lucide-react';

/* Brand icons — Lucide v1+ no longer ships brand SVGs, so we
   inline minimal versions here. They render at the same stroke
   weight defaults as the rest of the icon set. Only Facebook and
   Instagram are needed today; add new brand icons here when
   required. The components accept the same `size` / `strokeWidth`
   props as Lucide icons so they're interchangeable in usage. */
const Facebook = ({ size = 20, strokeWidth = 1.5, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);

const Instagram = ({ size = 20, strokeWidth = 1.5, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

export const Icon = {
  Cart: ShoppingCart,
  Wishlist: Heart,
  Menu,
  Close: X,
  ScrollUp: ArrowUp,
  Share: Share2,
  Search,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  ChevronUp,
  Account: User,
  Email: Mail,
  Phone,
  Location: MapPin,
  Plus,
  Minus,
  Check,
  AlertCircle,
  Success: CheckCircle2,
  Trash: Trash2,
  Edit: Edit2,
  ExternalLink,
  Copy,
  Filter,
  Language: Globe,
  Shipping: Truck,
  Cash: Banknote,
  Clock,
  Chat: MessageCircle,
  Filters: SlidersHorizontal,
  Sort: ArrowUpDown,
  Secure: ShieldCheck,
  Returns: RotateCcw,
  Star,
  Eye,
  Lock,
  Volume: Volume2,
  VolumeMute: VolumeX,
  Facebook,
  Instagram,
  // Admin sidebar (AdminLayout.js) — added as part of the Albania Launch
  // Readiness pass replacing emoji glyphs with the same Lucide set the
  // storefront already uses, for a calmer, more consistent admin UI.
  Dashboard: LayoutDashboard,
  Orders: ClipboardList,
  FiscalExport: Receipt,
  Products: Package,
  Collections: FolderOpen,
  Bundles: Gift,
  Popular: TrendingUp,
  AgeReview: Baby,
  Customers: Users,
  Discounts: Tag,
  Media: Image,
  Carousels: GalleryHorizontal,
  Marketing: Megaphone,
  Staff: UserCog,
  Settings,
  Help: HelpCircle,
  Store,
  Monitor,
  Tablet,
  Smartphone,
  Drag: GripVertical,
  Layers: Layers3,
  Save,
  Duplicate: CopyPlus,
  Hide: EyeOff,
  Inspector: PanelRight,
  Pages: FileText,
  Inventory: Boxes,
  Undo: Undo2,
  Redo: Redo2,
};

export const ICON_DEFAULTS = Object.freeze({
  strokeWidth: 1.5,
  size: 20,
});

export default Icon;
